/**
 * Что будет после ближайшего прихода: придёт, уйдёт, останется.
 *
 * Главная отвечала на вопрос «сколько до прихода» и молчала о том, что
 * дальше. У владельца 9 октября кредитка на 151 975 ₽, а 5–12 октября — ещё
 * пять платежей; сколько останется от зарплаты, приходилось складывать в
 * голове из четырёх разных блоков. Первый внешний пользователь попросил
 * ровно это: «две чаши весов — весь доход и весь расход».
 *
 * Считается отрезок в месяц от даты ближайшего прихода — по событиям общего
 * прогноза (`forecast.ts`), а не своим календарём. Внутри:
 *
 * · **придёт** — поступления по ритму дохода, **осторожной оценкой**, как и
 *   в проверке покупки. Прежде здесь стояла названная сумма с оговоркой, и
 *   один и тот же приход на соседних экранах стоил по-разному: у владельца
 *   290 000 ₽ здесь и 261 000 ₽ в прогнозе. Оговорка у плавающего дохода
 *   осталась — теперь она говорит, что сумма взята по нижней оценке;
 * · **платежи** — неоплаченное, что наступит в отрезке, **за вычетом уже
 *   отложенного**: под отложенное сегодняшние деньги уже заперты, и приход на
 *   них не нужен. Кредитки — оба платежа: остаток выписки и покупки после
 *   неё, если их срок попал в отрезок;
 * · **жизнь** — обычные траты по дням отрезка, как в прогнозе: месяц в 31
 *   день стоит на день дороже месяца в 30.
 *
 * Это прогноз, а не деньги: сюда не входит ничего из того, что уже на
 * счетах, и «Не закреплено» от него не меняется.
 */
import { ZERO, add, clampPos, sub, type Money } from './money.js';
import { addDays, addMonths, isAfter, isBefore, type PlainDate } from './date.js';
import { isKnown, type Amount } from './amount.js';
import { forecast, notCovered, type ForecastEvent } from './forecast.js';
import { nextForStream, type IncomeFact } from './income.js';
import { isPlanned, type PlannedAllocation } from './planned.js';
import type {
  Account, CreditTerms, IncomeStream, Obligation, Reserve,
} from './types.js';

export interface OutlookLine {
  label: string;
  date: PlainDate;
  amount: Money;
  /**
   * Покупки по карте после выписки: какой выпиской они уйдут в банк (04.11.2026).
   * До неё сумма — «пока»: человек ещё тратит и вносит. `label` у такой строки — имя карты.
   */
  statementDate?: PlainDate;
  /** Чей платёж — чтобы назначить на него приход (04.11.2026). */
  ref?: { kind: 'card'; accountId: string } | { kind: 'obligation'; instanceId: string; obligationId: string };
}

export interface IncomeOutlook {
  /** Первый день отрезка — дата ближайшего прихода. */
  from: PlainDate;
  /** Последний день отрезка. */
  to: PlainDate;
  /** Чей приход открывает отрезок — «ROWI», «Зарплата» (04.11.2026). */
  opener: string;
  /** Следующий приход того же дохода — конец отрезка; `null`, если его нет и отрезок — месяц. */
  until: PlainDate | null;
  income: Money;
  incomeLines: OutlookLine[];
  /** Есть ли среди прихода плавающие суммы: они взяты по нижней оценке. */
  variable: boolean;
  payments: Money;
  /** Платежи по убыванию суммы: крупные важнее для решения. */
  paymentLines: OutlookLine[];
  /**
   * Отложено из приходов отрезка на платежи следующего (04.11.2026): назначения
   * «Отложить на декабрь». Из этого отрезка они уходят, как платёж.
   */
  setAside: Money;
  setAsideLines: OutlookLine[];
  /** Обычная жизнь за отрезок; `null` — человек её не назвал. */
  living: Money | null;
  /** Что останется; `null`, пока жизнь неизвестна: иначе остаток завышен. */
  left: Money | null;
}

export function incomeOutlook(input: {
  today: PlainDate;
  /** Дата ближайшего прихода. Без неё отрезка нет. */
  horizon: PlainDate | null;
  accounts: readonly Account[];
  creditTerms: readonly CreditTerms[];
  incomeStreams: readonly IncomeStream[];
  obligations: readonly Obligation[];
  /** Конверты как записаны: прогноз сам урежет их до денег на своём счёте. */
  reserves: readonly Reserve[];
  needBaseline: Amount;
  wantBaseline: Amount;
  /** С какого дня приложение ведёт учёт: раньше платежи не считаются долгом. */
  trackingStartedAt?: PlainDate;
  /** Приходы, которые случились: пришедшее раньше срока второй раз не ждём. */
  incomeFacts?: readonly IncomeFact[];
  /**
   * Назначения из будущих приходов. Назначенное из прихода этого отрезка на
   * платёж за ним — расход отрезка; назначенное раньше на платёж в нём —
   * платёж уже закрывает (04.11.2026).
   */
  allocations?: readonly PlannedAllocation[];
}): IncomeOutlook | null {
  const from = input.horizon;
  // Приход ждут сегодня и ещё не записали — отрезок открывается им (05.11.2026).
  // Прежде в день зарплаты «После прихода» пропадала с главной до записи прихода.
  if (!from || isBefore(from, input.today)) return null;
  const to = addDays(addMonths(from, 1), -1);
  // Следующий приход ищем и чуть дальше месяца: ROWI 4 декабря (5-е — суббота) и 5 января — отрезок до 4 января, а не до 3-го.
  const reach = addDays(to, 7);

  /*
    Конверты расходуются по порядку сроков — с начала учёта, а не с даты
    прихода: так их раскладывает сам прогноз. Иначе конверт, отложенный под
    сегодняшний кредит авто, «покрыл» бы октябрьский, и из прихода вычлось бы
    на его сумму меньше правды.
  */
  const f = forecast({
    // Прогноз ведёт приходы с завтрашнего дня. Приход ждут сегодня — считаем
    // от вчера, иначе сегодняшний ROWI выпадал, а в отрезок попадал декабрьский.
    today: isBefore(input.today, from) ? input.today : addDays(from, -1),
    to: reach,
    accounts: input.accounts,
    creditTerms: input.creditTerms,
    incomeStreams: input.incomeStreams,
    obligations: input.obligations,
    reserves: input.reserves,
    needBaseline: isKnown(input.needBaseline) ? input.needBaseline.value : ZERO,
    wantBaseline: isKnown(input.wantBaseline) ? input.wantBaseline.value : ZERO,
    ...(input.trackingStartedAt ? { trackingStartedAt: input.trackingStartedAt } : {}),
    ...(input.incomeFacts ? { incomeFacts: input.incomeFacts } : {}),
  });
  /*
    Отрезок кончается накануне следующего прихода того же дохода, а не ровно
    через месяц (02.11.2026). ROWI 5-го с переносом на рабочий день: 5 декабря
    2026 — суббота, приход 4-го, и при «месяц минус день» он попадал внутрь
    отрезка «5 ноября – 4 декабря» вторым — доход месяца считался дважды.
  */
  const openers = new Set(f.events.filter((e) => e.kind === 'income' && e.date === from).map((e) => e.ref));
  const again = f.events
    .filter((e) => e.kind === 'income' && openers.has(e.ref) && isAfter(e.date, from) && !isAfter(e.date, reach))
    .map((e) => e.date).sort()[0];
  const end = again ? addDays(again, -1) : to;
  const opener = input.incomeStreams.filter((s) => openers.has(s.id)).map((s) => s.name).join(' и ') || 'Приход';
  const inside = f.events.filter((e) => !isBefore(e.date, from) && !isAfter(e.date, end));

  const incomeLines: OutlookLine[] = inside
    .filter((e) => e.kind === 'income')
    .map((e) => ({ label: e.label, date: e.date, amount: e.amount }));
  const variable = inside.some((e) => e.kind === 'income'
    && input.incomeStreams.some((s) => s.id === e.ref && s.isVariable));

  const planned = (input.allocations ?? []).filter(isPlanned);
  /** Назначения на этот платёж. */
  const aimedAt = (e: ForecastEvent) => planned.filter((a) => (e.kind === 'card'
    ? a.target.kind === 'card' && a.target.accountId === e.ref && a.target.dueDate === e.date
    : a.target.kind === 'obligation' && a.target.instanceId === e.instanceId));
  const paymentLines: OutlookLine[] = inside
    .filter((e) => e.kind === 'payment' || e.kind === 'card')
    .map((e) => {
      // Назначенное из прихода до отрезка к этому дню станет конвертом: из прихода отрезка его брать не нужно.
      const before = add(...aimedAt(e).filter((a) => isBefore(a.expectedDate, from)).map((a) => a.amount));
      const after = e.kind === 'card' && e.card?.part === 'after_statement' ? e.card : null;
      return {
        label: after ? input.accounts.find((a) => a.id === e.ref)?.name ?? e.label : e.label,
        ...(after?.statementDate ? { statementDate: after.statementDate } : {}),
        date: e.date, amount: clampPos(sub(notCovered(e), before)),
        ref: e.kind === 'card'
          ? { kind: 'card' as const, accountId: e.ref }
          : { kind: 'obligation' as const, instanceId: e.instanceId ?? `${e.ref}:${e.date}`, obligationId: e.ref },
      };
    })
    .filter((l) => l.amount > ZERO)
    .sort((a, b) => b.amount - a.amount);
  // Отложено из приходов отрезка на платежи после него.
  const dateOf = (a: PlannedAllocation): PlainDate | null => (a.target.kind === 'card' ? a.target.dueDate
    : a.target.kind === 'obligation' ? (a.target.instanceId.split(':')[1] as PlainDate | undefined) ?? null : null);
  const nameOf = (a: PlannedAllocation): string => (a.target.kind === 'card'
    ? input.accounts.find((x) => x.id === (a.target as { accountId: string }).accountId)?.name ?? 'Карта'
    : a.target.kind === 'obligation'
      ? input.obligations.find((o) => o.id === (a.target as { obligationId: string }).obligationId)?.name ?? 'Платёж'
      : 'Цель');
  const setAsideLines: OutlookLine[] = planned
    .filter((a) => !isBefore(a.expectedDate, from) && !isAfter(a.expectedDate, end))
    .filter((a) => { const d = dateOf(a); return d !== null && isAfter(d, end); })
    .map((a) => ({ label: nameOf(a), date: dateOf(a)!, amount: a.amount }));
  const setAside = add(...setAsideLines.map((l) => l.amount));

  const income = add(...incomeLines.map((l) => l.amount));
  const payments = add(...paymentLines.map((l) => l.amount));
  const living = isKnown(input.needBaseline)
    ? add(...inside.filter((e) => e.kind === 'living').map((e) => e.amount))
    : null;
  const left = living === null ? null : sub(sub(sub(income, payments), setAside), living);

  return {
    from, to: end, opener, until: again ?? null, income, incomeLines, variable, payments, paymentLines, setAside, setAsideLines, living, left,
  };
}

/**
 * Отрезок после текущего — от следующего прихода (04.11.2026).
 *
 * Покупки по карте после выписки уходят в платёж через месяц и в текущий
 * отрезок не попадают. У владельца 124 191 ₽ к 9 декабря нигде не было видно,
 * и декабрь после зарплаты оказывался в минусе без предупреждения.
 */
export function followingOutlook(
  input: Parameters<typeof incomeOutlook>[0], current: IncomeOutlook,
): IncomeOutlook | null {
  // `nextForStream` ищет строго после даты: от последнего дня отрезка — первый приход за ним.
  const next = input.incomeStreams
    .map((s) => nextForStream(s, current.to))
    .filter((d): d is PlainDate => d !== null && isAfter(d, current.to))
    .sort()[0];
  return next ? incomeOutlook({ ...input, horizon: next }) : null;
}

/** Остаток без знака минус — для строки «не хватит N». */
export const outlookGap = (o: IncomeOutlook): Money =>
  (o.left !== null && o.left < ZERO ? clampPos(sub(ZERO, o.left)) : ZERO);
